import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import VisitorService, { VisitorProps } from 'services/hooks/visitor-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import { Table } from '../Common/Table';
import Save from './VisitorForms/save';
import Delete from './VisitorForms/delete';
import { FilterSelect } from 'utils/FilterSelectUtils';
import ExportCSV from './VisitorForms/exportCSV';

export default function VisitorsSaveMode() {
  const [getVisitorsResult, setGetVisitorsResult] = useState<VisitorProps[] | null>([]);

  const {
    isLoading: isLoadingVisitors,
    refetch,
    data: visitorsData
  } = useQuery<VisitorProps[], Error>('visitors', VisitorService.findAllVisitors, {
    onError: (err: any) => {
      console.log(err.response?.data || err);
    }
  });

  useEffect(() => {
    if (visitorsData && Array.isArray(visitorsData)) {
      refetch();
      setGetVisitorsResult(visitorsData);
    }
  }, [visitorsData, refetch]);

  /* The `columns` constant is an array of objects that define the columns for the table in the `VisitorsSaveMode` component. Each object 
  represents a column and contains properties such as `id`, `Header`, `accessor`, `Cell`, and `disableFilters`. */
  const columns = useMemo(
    () => [
      {
        id: 'fullName',
        Header: 'Full Name',
        accessor: (row: { firstName: string; lastName: string }) => {
          return `${row.firstName} ${row.lastName}`;
        },
        Filter: FilterSelect
      },
      {
        id: 'phonenumber',
        Header: 'Phone Number',
        accessor: (row: { phoneNumber: string }) => {
          return `${row.phoneNumber}`;
        },
        disableFilters: true
      },
      {
        id: 'email',
        Header: 'Email',
        accessor: (row: { email: string }) => {
          return `${row.email}`;
        },
        disableFilters: true
      },
      {
        id: 'reason',
        Header: 'Reason',
        accessor: (row: { reason: string }) => {
          return `${row.reason}`;
        },
        disableFilters: true
      },
      {
        id: 'tag',
        Header: 'Tag',
        accessor: (row: { tag: string }) => {
          return `${row.tag}`;
        },
        Filter: FilterSelect
      },
      {
        id: 'company',
        Header: 'Company',
        accessor: (row: { company: string }) => {
          return `${row.company}`;
        },
        disableFilters: true
      },
      {
        id: 'createdAt',
        Header: 'Date',
        accessor: (row: { createdAt: string }) => {
          return `${new Date(row.createdAt).toLocaleDateString()}`;
        },
        disableFilters: true
      },
      {
        id: 'actions',
        Header: 'Actions',
        accessor: (data: VisitorProps) => data,
        Cell: (row: {
          value: VisitorProps;
          setEditUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
          setUpdateShowModal: React.Dispatch<React.SetStateAction<boolean>>;
          setDeleteUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
          setDeleteShowModal: React.Dispatch<React.SetStateAction<boolean>>;
        }) => {
          return (
            <div className="flex gap-4">
              <SecondaryButton
                onClick={() => {
                  row.setEditUUID(row.value.uuid);
                  row.setUpdateShowModal(true);
                }}
                buttonLabel="Edit"
                variant="default"
                size="px-4 w-auto"
              />
              <SecondaryButton
                onClick={() => {
                  row.setDeleteUUID(row.value.uuid);
                  row.setDeleteShowModal(true);
                }}
                buttonLabel="Delete"
                variant="default"
                size="px-4 w-auto"
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      }
    ],
    []
  );

  /* If the data is still loading or has not been fetched yet, the component returns a loading spinner component to indicate that the data is 
  being fetched. */
  if (isLoadingVisitors || !getVisitorsResult) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  /* The code is conditionally rendering either the `Table` component or a `LoadingSpinner` component based on the values of `isLoadingVisitors` 
  and `getVisitorsResult`. */
  return !isLoadingVisitors && getVisitorsResult ? (
    <div className="visitors">
      <Table
        name="Visitors"
        columns={columns}
        data={getVisitorsResult}
        refetch={refetch}
        CreateForm={Save}
        DeleteForm={Delete}
        ExportForm={ExportCSV}
        createQuery={VisitorService.createVisitor}
        updateQuery={VisitorService.updateVisitor}
        deleteQuery={VisitorService.deleteVisitor}
        editQuery={VisitorService.findVisitorById}
        queryName="visitors"
        buttonName="Visitor"
        toolTipMessage="Visitors are B-Line users without digital access cards. If you want your visitor to have a digital access card, visit the occupants page instead."
      />
    </div>
  ) : (
    <div>
      <LoadingSpinner />
    </div>
  );
}
